// Turns the pivoted metric-matrix output (dimensionA rows x dimensionB columns,
// single metric) into a flat list of cells with axis categories. Shared by the
// heatmap and sankey gadgets. Assumes useMetricMatrix was called with
// skip2DRowDataFormatting so cell values are raw numbers.

// Columns that don't carry a plottable cell value: the row-label/grouping
// column, the totals column, comparison delta columns, and raw helper columns.
const NON_DATA_SUFFIXES = ['-delta', '-percentDelta', '-raw'];

const flattenLeafColumns = (cols: MatrixColDef[]): MatrixColDef[] =>
  cols.flatMap((c) =>
    c.children && c.children.length ? flattenLeafColumns(c.children) : [c],
  );

interface MatrixCell {
  x: number;
  y: number;
  value: number;
}

const to2DMatrixCells = ({
  columnDefs,
  rowData,
  groupField,
  excludeFields = [],
}: {
  columnDefs: MatrixColDef[];
  rowData: MatrixRowData[];
  groupField: string;
  // Metric-id columns to drop. A genuine 2D matrix's value columns are the
  // dimensionB members; when the matrix collapses to 1D (dimensionB is a plain
  // categorical/range field the pipeline can't cross-tab) the only value column
  // is the metric itself. Dropping it means dataColumns is empty -> callers
  // render "No data" instead of surfacing the metric as an axis/node.
  excludeFields?: string[];
}): {
  xCategories: string[];
  yCategories: string[];
  cells: MatrixCell[];
} => {
  const dataColumns = flattenLeafColumns(columnDefs).filter(
    (c) =>
      !!c.field &&
      c.field !== groupField &&
      c.field !== 'total' &&
      !excludeFields.includes(c.field as string) &&
      !NON_DATA_SUFFIXES.some((suffix) => (c.field as string).endsWith(suffix)),
  );

  const yCategories = rowData.map((row) => String(row[groupField]));
  const xCategories = dataColumns.map((c) => c.headerName ?? c.field ?? '');

  const cells: MatrixCell[] = [];
  rowData.forEach((row, y) => {
    dataColumns.forEach((c, x) => {
      const value = Number(row[c.field as string]);
      if (!isNaN(value)) {
        cells.push({ x, y, value });
      }
    });
  });

  return { xCategories, yCategories, cells };
};

export default to2DMatrixCells;
